import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Globe, ArrowLeft, Clock, Calendar, Tag, Share2, Facebook, Twitter, Linkedin } from 'lucide-react';
import Footer from './Footer';
import SEO from './SEO';

const BLOG_POSTS = {
    "pdf-nasil-cevirilir": {
        title: "PDF Dosyası Nasıl Çevrilir? Adım Adım Rehber",
        date: "7 Mart 2025",
        readTime: "5 dk",
        tag: "Rehber",
        color: "blue",
        content: (
            <div className="space-y-6">
                <p className="text-lg leading-relaxed text-slate-700">
                    PDF belgeleri, formatı korunmuş yapısı nedeniyle düzenlenmesi ve çevrilmesi en zor dosya türlerinden biridir. Ancak günümüz teknolojisi ile bu işlem artık saniyeler sürüyor.
                </p>
                <h2 className="text-2xl font-bold text-slate-900">1. Dosyanızı Hazırlayın</h2>
                <p className="text-slate-700">
                    Çeviriye başlamadan önce PDF dosyanızın şifre korumalı olmadığından emin olun. Şifreli dosyalar güvenlik nedeniyle işlem göremez.
                </p>
                <h2 className="text-2xl font-bold text-slate-900">2. CeviriPro'ya Yükleyin</h2>
                <p className="text-slate-700">
                    Ana sayfadaki yükleme alanına dosyanızı sürükleyip bırakın. Sistemimiz anında dosya yapısını analiz edecek ve metin yoğunluğunu belirleyecektir.
                </p>
                <h2 className="text-2xl font-bold text-slate-900">3. Dil Seçimi ve İşlem</h2>
                <p className="text-slate-700">
                    Kaynak dili biliyorsanız seçin, bilmiyorsanız "Dili Algıla" özelliğini kullanın. Hedef dili seçtikten sonra "Çevir" butonuna tıklayarak işlemi başlatın.
                </p>
                <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-2xl my-8">
                    <p className="italic text-blue-800">
                        "Profesyonel İpucu: Çok büyük dosyalarda çeviri işlemi biraz zaman alabilir. İşlem bittiğinde size otomatik bir e-posta göndereceğiz."
                    </p>
                </div>
            </div>
        )
    },
    "ocr-teknolojisi-nedir": {
        title: "OCR Teknolojisi Nedir? Taranmış PDF'ler Nasıl İşlenir?",
        date: "5 Mart 2025",
        readTime: "7 dk",
        tag: "Teknoloji",
        color: "indigo",
        content: (
            <div className="space-y-6">
                <p className="text-lg leading-relaxed text-slate-700">
                    Optik Karakter Tanıma (OCR), resim halindeki metinleri makine tarafından okunabilir verilere dönüştüren devrim niteliğinde bir teknolojidir.
                </p>
                <h2 className="text-2xl font-bold text-slate-900">Neden OCR Gereklidir?</h2>
                <p className="text-slate-700">
                    Bazı PDF'ler doğrudan dijital olarak üretilmez; bunun yerine fiziksel bir kağıdın taranmasıyla oluşur. Bu tür belgelerde metinler aslında bir "resim" olduğu için standart kopyala-yapıştır yapılamaz.
                </p>
                <h2 className="text-2xl font-bold text-slate-900">CeviriPro OCR Nasıl Çalışır?</h2>
                <p className="text-slate-700">
                    Yüklediğiniz belgeyi piksel piksel tarayan yapay zekamız, harf şekillerini tanır ve bunları dijital metne dönüştürür. Bu işlem daha fazla işlem gücü gerektirdiği için token kullanımı %30 oranında artmaktadır.
                </p>
                <h2 className="text-2xl font-bold text-slate-900">Doğruluk Oranı</h2>
                <p className="text-slate-700">
                    Modern OCR sistemlerimiz el yazısı olmayan, net taranmış belgelerde %99'un üzerinde doğruluk payı ile çalışır. Düşük çözünürlüklü taramalarda bile optimize edilmiş modellerimiz en iyi sonucu vermeye odaklanır.
                </p>
            </div>
        )
    },
    "dokuman-ceviri-ipuclari": {
        title: "Doküman Çevirisinde Kaliteyi Artıracak 8 İpucu",
        date: "2 Mart 2025",
        readTime: "6 dk",
        tag: "İpuçları",
        color: "green",
        content: (
            <div className="space-y-6">
                <p className="text-lg leading-relaxed text-slate-700">
                    Otomatik çeviri araçlarını kullanırken sonuçların kalitesini artırmak sizin elinizde. İşte daha profesyonel sonuçlar için dikkat etmeniz gerekenler.
                </p>
                <ul className="list-disc pl-6 space-y-3 text-slate-700">
                    <li><strong>Görselleri Optimize Edin:</strong> Belge içindeki görseller çok karmaşıksa metin tespiti zorlaşabilir.</li>
                    <li><strong>Doğru Formatı Seçin:</strong> Metin tabanlı PDF'ler her zaman daha iyi sonuç verir.</li>
                    <li><strong>Yazım Yanlışlarını Kontrol Edin:</strong> Kaynak metindeki hatalar çeviriyi de bozar.</li>
                    <li><strong>Terim Listesi Kullanın:</strong> Teknik belgelerde anahtar kelimelerin tutarlılığı önemlidir.</li>
                </ul>
                <p className="text-slate-700">
                    Unutmayın ki otomatik çeviri bir taslak oluşturur. Çok kritik belgelerde bu taslağı bir insan gözüyle kontrol etmek her zaman en güvenlisidir.
                </p>
            </div>
        )
    },
    "epub-ceviri-rehberi": {
        title: "E-Kitap (EPUB) Çevirisi: En İyi Uygulamalar",
        date: "28 Şubat 2025",
        readTime: "4 dk",
        tag: "Rehber",
        color: "cyan",
        content: (
            <div className="space-y-6">
                <p className="text-lg leading-relaxed text-slate-700">
                    Sevdiğiniz bir e-kitabı kendi dilinizde okumak paha biçilemez. EPUB formatı, esnek yapısı sayesinde çeviri için en uygun formatlardan biridir.
                </p>
                <h2 className="text-2xl font-bold text-slate-900">EPUB'un Avantajları</h2>
                <p className="text-slate-700">
                    EPUB dosyaları, PDF'lerin aksine "akışkan" bir yapıya sahiptir. Bu, çevrilen metin uzasa bile sayfa yapısının bozulmayacağı ve okuyucunuzun yazı boyutuna göre kendini ayarlayacağı anlamına gelir.
                </p>
                <h2 className="text-2xl font-bold text-slate-900">Dosya Boyutu Sınırları</h2>
                <p className="text-slate-700">
                    Platformumuz 50MB'a kadar EPUB dosyalarını desteklemektedir. Çoğu e-kitap bu sınırın oldukça altındadır, ancak görselliği çok yüksek olan dosyalarda buna dikkat etmelisiniz.
                </p>
            </div>
        )
    }
};

const BlogPost = () => {
    const { slug } = useParams();
    const post = BLOG_POSTS[slug];
    const [copied, setCopied] = React.useState(false);

    const shareUrl = window.location.href;
    const shareTitle = post?.title || "CeviriPro Blog";

    const handleShare = (platform) => {
        let url = "";
        switch (platform) {
            case 'facebook':
                url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
                break;
            case 'twitter':
                url = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`;
                break;
            case 'linkedin':
                url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
                break;
            case 'copy':
                navigator.clipboard.writeText(shareUrl);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
                return;
            default:
                return;
        }
        window.open(url, '_blank', 'width=600,height=400');
    };

    if (!post) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-4xl font-bold mb-4">Post Bulunamadı</h1>
                    <Link to="/blog" className="text-blue-600 hover:underline">Blog listesine dön</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white">
            <SEO
                title={`${post.title} — CeviriPro Blog`}
                description={`CeviriPro Blog: ${post.title}. Çeviri teknolojileri, PDF işlemleri ve yapay zeka destekli belge çevirisi hakkında bilgi edinin.`}
                canonical={`/blog/${slug}`}
                schema={{
                    "@context": "https://schema.org",
                    "@type": "BlogPosting",
                    "headline": post.title,
                    "datePublished": post.date,
                    "author": { "@type": "Organization", "name": "CeviriPro" },
                    "publisher": {
                        "@type": "Organization",
                        "name": "CeviriPro",
                        "logo": { "@type": "ImageObject", "url": "https://ceviripro.com.tr/logo.png" }
                    },
                    "mainEntityOfPage": { "@type": "WebPage", "@id": `https://ceviripro.com.tr/blog/${slug}` }
                }}
            />
            {/* Nav */}
            <nav className="border-b border-slate-100 bg-white/80 backdrop-blur-sm sticky top-0 z-50">
                <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <div className="bg-gradient-to-r from-blue-600 to-green-600 p-2 rounded-xl">
                            <Globe className="w-5 h-5 text-white" />
                        </div>
                        <span className="font-bold text-slate-800 text-lg">CeviriPro</span>
                    </Link>
                    <div className="flex items-center gap-4">
                        <Link to="/blog" className="text-slate-600 hover:text-blue-600 text-sm font-medium transition-colors">Blog</Link>
                        <Link to="/register" className="px-4 py-2 bg-gradient-to-r from-blue-600 to-green-600 text-white text-sm font-semibold rounded-xl hover:shadow-lg transition-all">Ücretsiz Başla</Link>
                    </div>
                </div>
            </nav>

            <article className="py-16 px-4">
                <div className="max-w-3xl mx-auto">
                    <Link to="/blog" className="inline-flex items-center gap-2 text-slate-500 hover:text-blue-600 transition-colors mb-8 group">
                        <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                        Blog Listesine Dön
                    </Link>

                    <div className="mb-10">
                        <div className="flex items-center gap-4 mb-6">
                            <span className={`px-4 py-1.5 bg-blue-100 text-blue-700 rounded-full text-xs font-bold uppercase tracking-wider`}>
                                {post.tag}
                            </span>
                            <div className="flex items-center gap-1.5 text-slate-400 text-sm">
                                <Clock className="w-4 h-4" /> {post.readTime} okuma
                            </div>
                            <div className="flex items-center gap-1.5 text-slate-400 text-sm">
                                <Calendar className="w-4 h-4" /> {post.date}
                            </div>
                        </div>
                        <h1 className="text-4xl md:text-5xl font-black text-slate-900 leading-tight mb-8">
                            {post.title}
                        </h1>
                    </div>

                    <div className="prose prose-slate prose-lg max-w-none">
                        {post.content}
                    </div>

                    <div className="mt-16 pt-8 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-6 relative">
                        <div className="flex items-center gap-4">
                            <span className="text-slate-500 font-semibold">Paylaş:</span>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => handleShare('facebook')}
                                    className="p-2 bg-slate-50 rounded-full hover:bg-blue-50 hover:text-blue-600 transition-all shadow-sm"
                                    title="Facebook'ta Paylaş"
                                >
                                    <Facebook className="w-5 h-5" />
                                </button>
                                <button
                                    onClick={() => handleShare('twitter')}
                                    className="p-2 bg-slate-50 rounded-full hover:bg-sky-50 hover:text-sky-500 transition-all shadow-sm"
                                    title="Twitter'da Paylaş"
                                >
                                    <Twitter className="w-5 h-5" />
                                </button>
                                <button
                                    onClick={() => handleShare('linkedin')}
                                    className="p-2 bg-slate-50 rounded-full hover:bg-blue-100 hover:text-blue-700 transition-all shadow-sm"
                                    title="LinkedIn'de Paylaş"
                                >
                                    <Linkedin className="w-5 h-5" />
                                </button>
                                <button
                                    onClick={() => handleShare('copy')}
                                    className="p-2 bg-slate-50 rounded-full hover:bg-slate-100 hover:text-slate-900 transition-all shadow-sm relative"
                                    title="Bağlantıyı Kopyala"
                                >
                                    <Share2 className="w-5 h-5" />
                                    {copied && (
                                        <span className="absolute -top-10 left-1/2 -translate-x-1/2 px-2 py-1 bg-slate-800 text-white text-[10px] rounded whitespace-nowrap animate-bounce">
                                            Kopyalandı!
                                        </span>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </article>

            <Footer />
        </div>
    );
};

export default BlogPost;
